/**
 * Finance rule book results.
 *
 * Shows how the contract fared against each rule the finance team wrote:
 * breaches first, with the rule, the clause, the contract's own words and the
 * position finance wants instead. Severity comes from the rule, not the AI.
 */

import React, { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  HelpCircle,
  MinusCircle,
  ShieldAlert,
  XCircle
} from 'lucide-react';
import {
  FinanceRuleFinding,
  FinanceRuleReview,
  FinanceRuleVerdict
} from '../../../../types/contract.types';
import { ClassificationBadge } from './PlaybookReviewPanel';

const VERDICT_STYLES: Record<
  FinanceRuleVerdict,
  { label: string; badge: string; border: string; Icon: React.ElementType }
> = {
  VIOLATED: {
    label: 'Violated',
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
    border: 'border-red-300 dark:border-red-800',
    Icon: XCircle
  },
  UNCLEAR: {
    label: 'Needs review',
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    border: 'border-amber-300 dark:border-amber-800',
    Icon: HelpCircle
  },
  NOT_ADDRESSED: {
    label: 'Not addressed',
    badge: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
    border: 'border-gray-200 dark:border-dark-border',
    Icon: MinusCircle
  },
  COMPLIANT: {
    label: 'Compliant',
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
    border: 'border-emerald-300 dark:border-emerald-800',
    Icon: CheckCircle
  }
};

const ORDER: FinanceRuleVerdict[] = ['VIOLATED', 'UNCLEAR', 'NOT_ADDRESSED', 'COMPLIANT'];

const VerdictBadge: React.FC<{ verdict: FinanceRuleVerdict; className?: string }> = ({
  verdict,
  className = ''
}) => {
  const style = VERDICT_STYLES[verdict];
  const Icon = style.Icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${style.badge} ${className}`}>
      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
      {style.label}
    </span>
  );
};

const Label: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
    {children}
  </div>
);

const RuleFindingCard: React.FC<{ finding: FinanceRuleFinding; defaultOpen?: boolean }> = ({
  finding,
  defaultOpen = false
}) => {
  const [expanded, setExpanded] = useState(defaultOpen);
  const style = VERDICT_STYLES[finding.verdict];

  return (
    <div className={`border rounded-xl bg-white dark:bg-dark-surface ${style.border} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        className="w-full px-4 py-3 flex items-start gap-3 text-left hover:bg-gray-50 dark:hover:bg-dark-bg-secondary transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" />
        ) : (
          <ChevronRight className="w-4 h-4 mt-1 flex-shrink-0 text-gray-400" />
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900 dark:text-white">{finding.title}</span>
            {finding.clauseRef && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Clause {finding.clauseRef}</span>
            )}
            <span className="text-xs text-gray-400 dark:text-gray-500">{finding.category}</span>
          </div>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{finding.ruleText}</p>
        </div>

        <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
          <VerdictBadge verdict={finding.verdict} />
          {finding.verdict === 'VIOLATED' && <ClassificationBadge classification={finding.severity} />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-3 ml-7 space-y-3 text-sm border-t border-gray-100 dark:border-dark-border">
          <div>
            <Label>Rule</Label>
            <p className="text-gray-700 dark:text-gray-300">{finding.ruleText}</p>
          </div>

          {finding.evidenceQuote && (
            <div>
              <Label>Contract wording</Label>
              <blockquote className="border-l-2 border-gray-300 dark:border-dark-border pl-3 text-gray-700 dark:text-gray-300 italic">
                {finding.evidenceQuote}
              </blockquote>
              {!finding.evidenceVerified && (
                <p className="mt-1 text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1">
                  <AlertTriangle className="w-3.5 h-3.5" />
                  This quote could not be matched to the contract text. Check the clause directly.
                </p>
              )}
            </div>
          )}

          {finding.explanation && (
            <div>
              <Label>Assessment</Label>
              <p className="text-gray-700 dark:text-gray-300">{finding.explanation}</p>
            </div>
          )}

          {finding.suggestedPosition && finding.verdict !== 'COMPLIANT' && (
            <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3">
              <div className="text-xs font-semibold uppercase tracking-wide text-primary-700 dark:text-primary-300 mb-1">
                Our position
              </div>
              <p className="text-gray-800 dark:text-gray-200">{finding.suggestedPosition}</p>
            </div>
          )}

          <p className="text-xs text-gray-400 dark:text-gray-500">Rule version {finding.ruleVersion}</p>
        </div>
      )}
    </div>
  );
};

interface FinanceRuleReviewPanelProps {
  review: FinanceRuleReview;
}

export const FinanceRuleReviewPanel: React.FC<FinanceRuleReviewPanelProps> = ({ review }) => {
  const [showCompliant, setShowCompliant] = useState(false);

  const visible = showCompliant
    ? review.findings
    : review.findings.filter(f => f.verdict !== 'COMPLIANT');

  return (
    <div className="space-y-6">
      {review.incomplete && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4 text-sm text-amber-800 dark:text-amber-200">
          <ShieldAlert className="w-5 h-5 flex-shrink-0" />
          <p>
            Some rules could not be checked because the AI request failed. They are listed as
            needing review. Re-run the analysis to check them again.
          </p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {ORDER.map(verdict => {
          const style = VERDICT_STYLES[verdict];
          const Icon = style.Icon;
          return (
            <div key={verdict} className={`rounded-xl border p-3 ${style.border} bg-white dark:bg-dark-surface`}>
              <div className="flex items-center gap-2">
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span className="text-2xl font-semibold text-gray-900 dark:text-white">
                  {review.counts[verdict]}
                </span>
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">{style.label}</div>
            </div>
          );
        })}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Rules ({visible.length})
          </h4>
          {review.counts.COMPLIANT > 0 && (
            <button
              onClick={() => setShowCompliant(!showCompliant)}
              className="text-xs text-primary-600 dark:text-accent-400 hover:underline"
            >
              {showCompliant ? 'Hide' : 'Show'} {review.counts.COMPLIANT} compliant
            </button>
          )}
        </div>

        {visible.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            The contract complies with every rule in the rule book.
          </p>
        ) : (
          <div className="space-y-2">
            {visible.map(finding => (
              <RuleFindingCard
                key={finding.ruleId}
                finding={finding}
                defaultOpen={finding.verdict === 'VIOLATED' && finding.severity === 'CRITICAL'}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FinanceRuleReviewPanel;
